import React, { ReactNode } from 'react'
import { PrimeReactProvider } from 'primereact/api'
import { configs, getFaviconLinksForThemeBrand, getGtmContainerIdForThemeBrand } from '@configs'
import { ModalProvider } from '@hooks/useModal'
import { OktaProvider } from '@hooks/useOkta'
import { MkSiteProvider, useSiteContext } from '@contexts/MkSiteContext'
import { AuthenticationProvider } from '@auth/contexts/core'
import { RegisterProvider } from '@account/contexts/register'
import { SignInProvider } from '@account/contexts/signin'
import { AuthLabelsProvider } from '@features/account/contexts/AuthLabelsContext'
import { AvailabilityProvider } from '@search/context'
import { ResortProvider } from '@resort/context/ResortContext'
import { ResortPageProvider } from '@resort/context/ResortPageContext'
import { BookProvider } from '@book/contexts/core'
import { WaitlistProvider } from '@features/account'
import { MeProvider } from '@contexts/MeContext'
import { SnackbarProvider } from '@contexts/SnackbarContext'
import { DevUiProvider } from '@utils/DevUIPanel/DevUiProvider'
import { HelmetProvider } from 'react-helmet-async'
import PageMeta from '@layout/PageMeta'
import { SessionAwareAccountProvider } from '@account/contexts/SessionAwareAccountProvider'
import { AnalyticsBootstrapProvider, AnalyticsProvider } from '@analytics/context'
import { brandDisplayName } from '@typescript'
import AuthErrorHandler from './AuthErrorHandler'

export interface AppProvidersProps {
  children: ReactNode
}

// A utility function to combine multiple providers
const buildProvidersTree =
  (providers: Array<[React.ComponentType<any>, any?]>) =>
  ({ children }: AppProvidersProps) =>
    providers.reduceRight((acc, [Provider, props = {}]) => <Provider {...props}>{acc}</Provider>, children)

/**
 * AppProviders component that wraps the application with all necessary context providers
 * This component centralizes all provider setup in one place, making the main app component cleaner
 * [HZ] 2025-08-28: Note that the order of the providers is important.
 */
export const AppProviders = buildProvidersTree([
  [MkSiteProvider],
  [OktaProvider],
  [HelmetProvider],
  [
    PrimeReactProvider,
    {
      value: {
        unstyled: true,
        pt: configs.primereact,
      },
    },
  ],
  [SnackbarProvider],
  [DevUiProvider],
  [AuthenticationProvider],
  [MeProvider],
  [AnalyticsBootstrapProvider],
  [RegisterProvider],
  [SignInProvider],
  [AuthLabelsProvider],
  [ModalProvider],
  [
    function AuthErrorWrapper({ children }: { children: React.ReactNode }) {
      // Unified auth error handler - handles both events and URL parameters
      const { themeBrand } = useSiteContext()
      return (
        <>
          <AuthErrorHandler />
          <PageMeta
            gtmId={getGtmContainerIdForThemeBrand(themeBrand)}
            links={getFaviconLinksForThemeBrand(themeBrand)}
            meta={{ title: brandDisplayName(themeBrand) }}
          />
          {children}
        </>
      )
    },
  ],
  [AvailabilityProvider],
  [ResortProvider],
  [ResortPageProvider],
  [BookProvider],
  [WaitlistProvider],
  [SessionAwareAccountProvider],
  [AnalyticsProvider],
])
